import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { VideoAnnotation } from "../types";
import { useBufferAudioSync } from "../hooks/useBufferAudioSync";
import LogoGSCInline from "./LogoGSCInline";
import { AreaChart, Area, ResponsiveContainer } from "recharts";

interface PostureGridProps {
  data?: any;
  refVideoUrl?: string;
  stuVideoUrl?: string;
  annotations?: VideoAnnotation[];
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onsets?: number[];
  stuEnvelope?: number[];
}

/**
 * Vision Lab - WebAudio Master Sync (silent)
 * ✅ AudioContext truth
 * ✅ output muted
 * ✅ video is visual only (event-driven drift correction)
 */
export const PostureGrid: React.FC<PostureGridProps> = ({
  data,
  refVideoUrl,
  stuVideoUrl,
  annotations = [],
  isFullscreen = false,
  onToggleFullscreen,
  onsets = [],
  stuEnvelope = [],
}) => {
  const [layout] = useState<"solo" | "split-v" | "mirror">("split-v");
  const [manualMicroSync] = useState(0); // ms (tu peux réactiver plus tard)

  const stuVideo = useRef<HTMLVideoElement>(null);
  const refVideo = useRef<HTMLVideoElement>(null);

  const hasRefVideo = !!refVideoUrl && refVideoUrl.trim().length > 0;
  const hasStuVideo = !!stuVideoUrl && stuVideoUrl.trim().length > 0;

  const {
    status,
    isPlaying,
    currentTime,
    togglePlay,
    seek,
    setOutputGain,
  } = useBufferAudioSync({
    stuUrl: stuVideoUrl || "",
    refUrl: hasRefVideo ? refVideoUrl : undefined,
    refOffsetMs: manualMicroSync,
    playbackSpeed: 1.0,
    outputGain: 0, // ✅ VisionLab silencieux
  });

  // Force sécurité
  useEffect(() => {
    setOutputGain(0);
  }, [setOutputGain]);

  // ---------------------------
  // VIDEO SYNC (event-driven, no RAF)
  // ---------------------------
  const DRIFT_MAX = 0.25; // sec
  const COOLDOWN_MS = 800;
  const lastFixMsRef = useRef(0);

  const forceVideoTime = useCallback((t: number) => {
    const sv = stuVideo.current;
    const rv = refVideo.current;

    const tStu = Math.max(0, t);
    const tRef = Math.max(0, t + manualMicroSync / 1000);

    if (sv && sv.readyState >= 2) {
      try { sv.currentTime = tStu; } catch {}
    }
    if (hasRefVideo && rv && rv.readyState >= 2) {
      try { rv.currentTime = tRef; } catch {}
    }

    lastFixMsRef.current = performance.now();
  }, [hasRefVideo, manualMicroSync]);

  const maybeFixDrift = useCallback(() => {
    if (!isPlaying) return;

    const now = performance.now();
    if (now - lastFixMsRef.current < COOLDOWN_MS) return;

    const t = currentTime;
    const tRef = Math.max(0, t + manualMicroSync / 1000);

    const sv = stuVideo.current;
    if (sv && sv.readyState >= 2) {
      const d = t - sv.currentTime;
      if (Math.abs(d) > DRIFT_MAX) {
        try { sv.currentTime = Math.max(0, t); } catch {}
        lastFixMsRef.current = now;
        return;
      }
    }

    const rv = refVideo.current;
    if (hasRefVideo && rv && rv.readyState >= 2) {
      const d = tRef - rv.currentTime;
      if (Math.abs(d) > DRIFT_MAX) {
        try { rv.currentTime = tRef; } catch {}
        lastFixMsRef.current = now;
      }
    }
  }, [isPlaying, currentTime, hasRefVideo, manualMicroSync]);

  // Security: forbid loop
  useEffect(() => {
    const sv = stuVideo.current;
    const rv = refVideo.current;
    if (sv) sv.loop = false;
    if (rv) rv.loop = false;
  }, [hasStuVideo, hasRefVideo]);

  // Play/Pause mirror + HARD align once when starting
  useEffect(() => {
    const sv = stuVideo.current;
    const rv = refVideo.current;

    const apply = async () => {
      if (!sv) return;

      try {
        sv.muted = true;
        sv.playsInline = true;
        sv.loop = false;
      } catch {}

      if (hasRefVideo && rv) {
        try {
          rv.muted = true;
          rv.playsInline = true;
          rv.loop = false;
        } catch {}
      }

      if (isPlaying) {
        // hard align ONCE at play
        forceVideoTime(currentTime);

        try { await sv.play(); } catch {}
        if (hasRefVideo && rv) {
          try { await rv.play(); } catch {}
        }
      } else {
        try { sv.pause(); } catch {}
        if (hasRefVideo && rv) {
          try { rv.pause(); } catch {}
        }
      }
    };

    void apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, hasRefVideo]);

  // Attach event-driven drift correction (NO RAF)
  useEffect(() => {
    const sv = stuVideo.current;
    const rv = refVideo.current;

    const handler = () => maybeFixDrift();

    const bind = (v?: HTMLVideoElement | null) => {
      if (!v) return () => {};
      v.addEventListener("timeupdate", handler);
      v.addEventListener("waiting", handler);
      v.addEventListener("stalled", handler);
      v.addEventListener("seeked", handler);
      v.addEventListener("playing", handler);
      return () => {
        v.removeEventListener("timeupdate", handler);
        v.removeEventListener("waiting", handler);
        v.removeEventListener("stalled", handler);
        v.removeEventListener("seeked", handler);
        v.removeEventListener("playing", handler);
      };
    };

    const unStu = bind(sv);
    const unRef = hasRefVideo ? bind(rv) : () => {};

    return () => {
      unStu();
      unRef();
    };
  }, [hasRefVideo, maybeFixDrift]);

  // Wrap seek/toggle to hard-align exactly ON SEEK/PLAY (single write)
  const seekAndAlign = useCallback(async (t: number) => {
    await seek(t);
    forceVideoTime(t);
  }, [seek, forceVideoTime]);

  const toggleAndAlign = useCallback(async () => {
    if (!isPlaying) {
      forceVideoTime(currentTime);
    }
    await togglePlay();
  }, [isPlaying, currentTime, togglePlay, forceVideoTime]);

  // ---------------------------
  // Existing calculations (kept)
  // ---------------------------
  const isLocked = status === "playing" || status === "ready";

  const currentAudioEnergy = useMemo(() => {
    const dur = stuVideo.current?.duration || 0;
    if (!stuEnvelope.length || !dur) return 0;

    const progress = currentTime / dur;
    const idx = Math.max(
      0,
      Math.min(stuEnvelope.length - 1, Math.floor(progress * (stuEnvelope.length - 1)))
    );
    return stuEnvelope[idx] || 0;
  }, [currentTime, stuEnvelope]);

  const kinematicData = useMemo(() => {
    const stuEnv = data?.kinematicEnvelopeStu || [];
    const refEnv = data?.kinematicEnvelopeRef || [];
    const points = Math.max(stuEnv.length, refEnv.length);

    const chart: Array<{ time: number; stu: number; ref: number }> = [];
    for (let i = 0; i < points; i++) {
      chart.push({ time: i * 0.1, stu: stuEnv[i] || 0, ref: refEnv[i] || 0 });
    }
    return chart;
  }, [data]);

  if (!hasStuVideo) {
    return (
      <div
        className={`flex flex-col h-full bg-[#010203] ${
          isFullscreen ? "" : "rounded-[48px] border border-white/10 shadow-3xl"
        }`}
      >
        <div className="p-10 text-white/80">
          <div className="text-xl font-black mb-2">Vision Lab</div>
          <div className="text-sm text-white/50">
            stuVideoUrl manquant — impossible de démarrer la lecture.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full bg-[#010203] relative overflow-hidden transition-all duration-500 ${
        isFullscreen ? "" : "rounded-[48px] border border-white/10 shadow-3xl"
      }`}
    >
      <div className="flex-1 flex flex-col md:flex-row bg-black overflow-hidden relative">
        <div className="absolute top-8 right-8 z-[60] pointer-events-none opacity-20">
          <LogoGSCInline size={50} className="text-white" />
        </div>

        {layout === "split-v" && (
          <div className="flex-1 relative border-r border-white/10 bg-[#050505]">
            {hasRefVideo ? (
              <video
                ref={refVideo}
                src={refVideoUrl}
                className="w-full h-full object-cover contrast-110 grayscale-[0.3]"
                muted
                playsInline
                preload="auto"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center opacity-40">
                <span className="text-6xl mb-4">👨‍🏫</span>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Master Audio Only
                </span>
              </div>
            )}
            <div className="absolute bottom-8 left-8 px-5 py-2 bg-black/80 backdrop-blur-xl rounded-full text-[10px] font-black text-smart-gold uppercase border border-smart-gold/30 z-20">
              Target Motion
            </div>
          </div>
        )}

        <div className="flex-1 relative bg-black">
          <video
            ref={stuVideo}
            src={stuVideoUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="auto"
          />
          <div className="absolute bottom-8 right-8 px-5 py-2 bg-smart-accent/90 backdrop-blur-xl rounded-full text-[10px] font-black text-white uppercase border border-white/20 z-20">
            Live Take
          </div>
        </div>

        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-40 w-full max-w-xl px-4 pointer-events-none">
          <div className="bg-black/90 backdrop-blur-2xl p-6 rounded-[32px] border border-white/20 shadow-3xl pointer-events-auto flex flex-col gap-4">
            <div className="flex gap-6 items-center">
              <div className="flex flex-col items-center gap-1 shrink-0">
                <div
                  className={`w-16 h-16 rounded-2xl bg-black flex items-center justify-center text-2xl font-black italic border-2 ${
                    data?.score > 80 ? "border-emerald-500 text-emerald-400" : "border-smart-gold text-smart-gold"
                  }`}
                >
                  {data?.score || 0}%
                </div>
                <span className="text-[8px] font-black text-slate-500 uppercase">Gestalt Match</span>
              </div>

              <div className="flex-1">
                <h4 className="text-[9px] font-black text-cyan-400 uppercase tracking-[0.3em]">
                  Video DSP Kernel v2.0
                </h4>
                <p className="text-xs text-slate-200 italic leading-relaxed truncate">
                  {data?.humanFeedback || "Comparaison des ondes cinématiques..."}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between text-[10px] text-slate-400">
              <span>{status === "loading" ? "Loading audio..." : ""}</span>
              <span>{status === "error" ? "Audio error" : ""}</span>
              <span>{isLocked ? "Sync engine active" : ""}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#050505] border-t border-white/10 p-6 md:p-8 flex flex-col gap-6 shrink-0">
        <div className="relative h-24 w-full bg-black rounded-3xl border border-white/5 overflow-hidden group">
          <div className="absolute inset-0 z-10 pointer-events-none">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={kinematicData}>
                <defs>
                  <linearGradient id="visRef" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopOpacity={0.2} />
                    <stop offset="100%" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="visStu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopOpacity={0.4} />
                    <stop offset="100%" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="ref" strokeWidth={1} fill="url(#visRef)" isAnimationActive={false} />
                <Area type="monotone" dataKey="stu" strokeWidth={2} fill="url(#visStu)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_20px_white] z-30"
            style={{ left: `${(currentTime / (stuVideo.current?.duration || 1)) * 100}%` }}
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-center">
          <div className="flex items-center gap-6 shrink-0">
            <button
              onClick={toggleAndAlign}
              className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center text-xl shadow-2xl transition-all hover:scale-105"
            >
              {isPlaying ? "⏸" : "▶"}
            </button>
            <div className="flex flex-col">
              <span className="text-2xl font-mono font-black text-white italic">
                {(currentTime || 0).toFixed(2)}s
              </span>
              {isLocked && (
                <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">
                  Sync Engine Active
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 p-4 rounded-3xl border border-white/5 flex flex-col justify-center">
              <div className="flex justify-between mb-1">
                <span className="text-[8px] font-black text-slate-500 uppercase">Kinematic Sync</span>
                <span className="text-[8px] font-black text-cyan-400">
                  {((data?.motionReliability || 0) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-black/40 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${(data?.motionReliability || 0) * 100}%` }} />
              </div>
            </div>

            <button
              onClick={onToggleFullscreen}
              className="h-14 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all font-black text-[9px] uppercase tracking-widest"
            >
              {isFullscreen ? "QUIT LAB" : "EXPAND VIDEO LAB ⛶"}
            </button>

            <button
              onClick={() => seekAndAlign(0)}
              className="h-14 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all font-black text-[9px] uppercase tracking-widest md:col-span-2"
            >
              RESET (SEEK 0)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
